import { EWorkflowStatusDataBlock } from "constants/index";
import { FeatureTypes } from "entity-app/constants";
import { getImplicitMaterialDisplayTitle } from "entity-app/shared-components/entity-dateblock/utils/implicit-material-title";
import {
  RawMilestone,
  RawOffset,
  RawInstance,
  RawLink,
  RawDependencyPayload
} from "./raw-model";
import {
  MilestoneNode,
  GoverningActivity,
  DurationSegment,
  BidPackageRow,
  BidRiskLevel,
  BidWorkflowStatus,
  DependencyModel,
  LeadTimeLine,
  SiteActivityLink,
  SiteMaterialLink,
  SiteSubmittalLink
} from "./model";

export function mapMilestones(
  raw: RawMilestone[] | null | undefined,
  opts: { planOnly?: boolean } = {}
): MilestoneNode[] {
  return (raw ?? [])
    .filter((m) => !m.hidden)
    .map((m) => {
      const plannedDate = m.planned_date ?? null;
      const committedDate = m.committed_date ?? null;
      const projectedDate = m.projected_date ?? null;
      const actualDate = m.actual_date ?? null;
      // Bid packages position by plan only. A design milestone positions by its
      // COMMITTED date and nothing else — the plan is an untouched baseline and
      // an actual date records what happened, neither is the commitment the
      // timeline communicates. A milestone with no committed date lands in the
      // undated tray instead (see computeGeometry).
      const effectiveDate = opts.planOnly ? plannedDate : committedDate;
      const srcOffset = m.feature_workflow_instance_offsets_as_src?.[0];
      return {
        id: m.id ?? "",
        name: m.name ?? "",
        sequenceNo: m.sequence_no ?? 0,
        plannedDate,
        committedDate,
        projectedDate,
        actualDate,
        effectiveDate,
        action: m.milestone_action ?? "",
        offset: srcOffset
          ? {
              name: srcOffset.name ?? "",
              duration: srcOffset.duration ?? 0
            }
          : null
      };
    })
    .sort((a, b) => a.sequenceNo - b.sequenceNo);
}

/** Days between design milestones come from workflow_instance_offsets.
 *  An offset belongs to the milestone it starts from. Report-engine rows
 *  may arrive out of order; sequence_no is the join key when present. */
export function applyWorkflowOffsets(
  milestones: MilestoneNode[],
  offsets: RawOffset[] | null | undefined
): MilestoneNode[] {
  if (!offsets?.length) return milestones;
  const joinedById = offsets.some(
    (offset) => offset.from_milestone_instance_id
  );
  const sequenced = offsets.some((offset) => offset.sequence_no != null);
  const bySequence = sequenced
    ? [...offsets].sort((a, b) => (a.sequence_no ?? 0) - (b.sequence_no ?? 0))
    : offsets;
  const sequenceJoinsMilestone = sequenced
    ? offsets.some((offset) =>
        milestones.some(
          (milestone) => milestone.sequenceNo === offset.sequence_no
        )
      )
    : false;
  return milestones.map((milestone, index) => {
    const match = joinedById
      ? offsets.find(
          (offset) => offset.from_milestone_instance_id === milestone.id
        )
      : sequenceJoinsMilestone
        ? offsets.find((offset) => offset.sequence_no === milestone.sequenceNo)
        : bySequence[index];
    const duration = match?.duration;
    if (!match || duration == null || Number.isNaN(Number(duration))) {
      return joinedById || sequenceJoinsMilestone
        ? { ...milestone, offset: null }
        : milestone;
    }
    return {
      ...milestone,
      offset: {
        name: match.name ?? "",
        duration: Number(duration)
      }
    };
  });
}

// One submittal → material pair (SP 1:1 MP). A material fans out to n activities.
export interface PlanningPair {
  submittal: RawInstance | null;
  material: RawInstance | null;
  activityLinks: RawLink[];
}

// A bid package fans out to n pairs: BP 1:n (SP 1:1 MP) 1:n AP. A pair may have
// no submittal (a material- or activity-only link).
export function getPairs(bp: RawInstance | null | undefined): PlanningPair[] {
  const submittalLinks = bp?.linkedSubmittals ?? [];
  if (submittalLinks.length > 0) {
    return submittalLinks.map((sl) => {
      const submittal = sl.target_feature_instance ?? null;
      const material =
        submittal?.linkedMaterials?.[0]?.target_feature_instance ?? null;
      return {
        submittal,
        material,
        activityLinks: [
          ...activityLinksOf(material),
          ...activityLinksOf(submittal)
        ]
      };
    });
  }
  // No submittal hop — the bid package may link a material directly.
  const materialLinks = bp?.linkedMaterials ?? [];
  if (materialLinks.length > 0) {
    return materialLinks.map((ml) => {
      const material = ml.target_feature_instance ?? null;
      return {
        submittal: null,
        material,
        activityLinks: activityLinksOf(material)
      };
    });
  }
  const directActivities = activityLinksOf(bp);
  if (directActivities.length > 0) {
    return [
      {
        submittal: null,
        material: null,
        activityLinks: directActivities
      }
    ];
  }
  return [];
}

const LINK_TYPE_FINISH_TO_START = 0;

function materialOf(pair: PlanningPair): { id: string; name: string } | null {
  return pair.material
    ? { id: pair.material.id ?? "", name: pair.material.title ?? "" }
    : null;
}

function submittalOf(pair: PlanningPair): { id: string; name: string } | null {
  return pair.submittal
    ? { id: pair.submittal.id ?? "", name: pair.submittal.title ?? "" }
    : null;
}

// "Required on Site" is the LEAST (earliest) of every linked activity date AND
// the manually "set date" (a planning-only-activity's manual_entry_date) — the
// governing date can come from either an activity link or a set date, whichever
// is earlier. A governing_link flag (the backend's explicit marker) wins when
// present; otherwise we take the minimum across activities and set dates. An
// activity link contributes its start_date (finish-to-start) or else end_date.
interface GovCandidate {
  instance: RawInstance;
  pair: PlanningPair;
  date: string; // the date that feeds Required on Site
  endDate: string | null;
  isManual: boolean;
  dateKind: "start" | "end";
  flagged: boolean;
}

// Fallback when no linked activity governs: if a linked material carries a
// final_deadline_date, govern off that instead (earliest across materials), so
// the activity box still shows the bid package's required-on-site deadline.
function resolveMaterialDeadline(
  pairs: PlanningPair[]
): GoverningActivity | null {
  const matCandidates = pairs.flatMap((pair) => {
    const mat = pair.material;
    if (!mat) return [];
    const wf = mat.feature_workflow_instance;
    return [wf?.final_deadline_date, wf?.manual_entry_date]
      .filter((deadline): deadline is string => Boolean(deadline))
      .map((deadline) => ({ pair, mat, deadline }));
  });

  if (matCandidates.length === 0) return null;

  const chosen = matCandidates
    .slice()
    .sort((a, b) => Date.parse(a.deadline) - Date.parse(b.deadline))[0];

  return {
    id: chosen.mat.id ?? "",
    // raw material name; the renderer shows "<name> (Deadline)" for
    // MATERIAL_DEADLINE, not this name alone.
    name: chosen.mat.title ?? "",
    startDate: chosen.deadline,
    endDate: chosen.deadline,
    isDeleted: !!chosen.mat.deleted_from_source,
    source: "MATERIAL_DEADLINE",
    dateKind: "start",
    material: materialOf(chosen.pair),
    submittal: submittalOf(chosen.pair)
  };
}

export function resolveGoverning(
  pairs: PlanningPair[]
): GoverningActivity | null {
  const candidates: GovCandidate[] = [];

  pairs.forEach((pair) => {
    (pair.activityLinks ?? []).forEach((link) => {
      const inst = linkTarget(link);
      if (!inst) return;
      const flagged = !!link.governing_link;
      const fs =
        (link.link_type ?? LINK_TYPE_FINISH_TO_START) ===
        LINK_TYPE_FINISH_TO_START;
      const activities = inst.feature_activities ?? [];

      if (activities.length > 0) {
        // a real activity link — every activity it carries is a candidate
        activities.forEach((a) => {
          const date = (fs ? a.start_date : a.end_date) ?? null;
          if (date) {
            candidates.push({
              instance: inst,
              pair,
              date,
              endDate: a.end_date ?? null,
              isManual: false,
              dateKind: fs ? "start" : "end",
              flagged
            });
          }
        });
      } else if (
        Number(inst.feature_type_id) ===
        (FeatureTypes.PLANNING_ONLY_ACTIVITY as number)
      ) {
        // a manually "set date" estimate
        const manualDate =
          inst.feature_workflow_instance?.manual_entry_date ?? null;
        if (manualDate) {
          candidates.push({
            instance: inst,
            pair,
            date: manualDate,
            endDate: manualDate,
            isManual: true,
            dateKind: "start",
            flagged
          });
        }
      }
    });
  });

  // No governing activity — fall back to a linked material's deadline date.
  if (candidates.length === 0) return resolveMaterialDeadline(pairs);

  // The backend's explicit governing flag wins; otherwise the earliest date.
  const chosen =
    candidates.find((c) => c.flagged) ??
    candidates
      .slice()
      .sort((a, b) => Date.parse(a.date) - Date.parse(b.date))[0];

  return {
    id: chosen.instance.id ?? "",
    name: chosen.instance.title ?? "",
    startDate: chosen.date,
    endDate: chosen.endDate,
    isDeleted: !!chosen.instance.deleted_from_source,
    source: chosen.isManual ? "MANUAL" : "ACTIVITY_LINK",
    dateKind: chosen.dateKind,
    material: materialOf(chosen.pair),
    submittal: submittalOf(chosen.pair)
  };
}

function sumOffsets(offsets: RawInstance["workflow_instance_offsets"]): number {
  return (offsets ?? []).reduce((acc, o) => acc + (o.duration ?? 0), 0);
}

// Lead time = MAX over pairs of (submittal-plan duration + material-plan
// duration), each being the sum of that instance's offsets. The breakdown shown
// is the governing (max) pair's. (BP 1:n → the longest pair governs.)
export function resolveDurations(pairs: PlanningPair[]): {
  segments: DurationSegment[];
  totalDays: number;
} {
  let bestTotal = 0;
  let bestSub = 0;
  let bestMat = 0;
  pairs.forEach((pair) => {
    const sub = sumOffsets(pair.submittal?.workflow_instance_offsets);
    const mat = sumOffsets(pair.material?.workflow_instance_offsets);
    if (sub + mat > bestTotal) {
      bestTotal = sub + mat;
      bestSub = sub;
      bestMat = mat;
    }
  });

  if (bestTotal <= 0) return { segments: [], totalDays: 0 };

  const segments: DurationSegment[] = [];
  if (bestSub > 0) {
    segments.push({ key: "SUBMITTAL", label: "Submittal Plan", days: bestSub });
  }
  if (bestMat > 0) {
    segments.push({
      key: "MATERIAL_PLAN",
      label: "Material Plan",
      days: bestMat
    });
  }
  return { segments, totalDays: bestTotal };
}

// Which DP milestone a bid-package link targets. Prefers the explicit
// CIQ-11498 reference (source_feature_milestone), GUARDED by matching its id
// against the DP's own milestones — so a wrong-direction reference (e.g. a BP
// milestone) can never mislabel the row. Falls back to the legacy
// linked_offset_date same-day match for links created before the feature, and
// returns null when neither resolves.
function findLinkedMilestone(
  link: RawLink,
  dpMilestones: MilestoneNode[]
): { id: string; name: string } | null {
  const ref = link.source_feature_milestone?.id;
  if (ref) {
    const m = dpMilestones.find((dm) => dm.id === ref);
    if (m) return { id: m.id, name: m.name };
  }
  const key = (link.linked_offset_date ?? "").slice(0, 10);
  if (key) {
    const m = dpMilestones.find(
      (dm) => (dm.effectiveDate ?? "").slice(0, 10) === key
    );
    if (m) return { id: m.id, name: m.name };
  }
  return null;
}

// "YYYY-MM-DD…" → integer day number (UTC, deterministic).
function dayNum(d: string): number {
  const [y, m, day] = d.slice(0, 10).split("-").map(Number);
  return Math.floor(Date.UTC(y, m - 1, day) / 86_400_000);
}

// How far a bid package's plan extends outside the design span — the excess time
// hidden inside the compressed time-jump, so it can be highlighted per row.
function outOfScopeOf(
  milestones: MilestoneNode[],
  span: { start: number; end: number } | null
): BidPackageRow["outOfScope"] {
  if (!span) return null;
  const days = milestones
    .map((m) => m.effectiveDate)
    .filter((d): d is string => !!d)
    .map(dayNum);
  if (!days.length) return null;
  const left = span.start - Math.min(...days); // starts before the span
  const right = Math.max(...days) - span.end; // ends after the span
  if (right > 0 && right >= left) return { days: right, side: "right" };
  if (left > 0) return { days: left, side: "left" };
  return null;
}

function submittalLeadLabel(
  raw: string,
  lower: string,
  style: "plan" | "plain"
): string {
  const prep = lower.startsWith("prep") || lower.startsWith("pre");
  const review = lower.startsWith("review");
  if (style === "plan")
    return plannedSubmittalLeadLabel(raw, lower, prep, review);
  return plainSubmittalLeadLabel(raw, lower, prep, review);
}

function plannedSubmittalLeadLabel(
  raw: string,
  lower: string,
  prep: boolean,
  review: boolean
): string {
  if (prep) return "Submittal Plan's Prep Time";
  if (review) return "Submittal Plan's Review Time";
  if (lower.startsWith("submittal")) return raw;
  return raw ? `Submittal Plan's ${raw}` : "Submittal Plan";
}

function plainSubmittalLeadLabel(
  raw: string,
  lower: string,
  prep: boolean,
  review: boolean
): string {
  if (prep) return "Submittal Prep Time";
  if (review) return "Submittal Review Time";
  if (lower.startsWith("submittal")) return raw;
  return raw ? `Submittal ${raw}` : "Submittal";
}

function leadLabel(
  kind: "submittal" | "material",
  name: string | null | undefined,
  style: "plan" | "plain"
): string {
  const raw = (name ?? "").trim();
  const lower = raw.toLowerCase();
  if (kind === "submittal") return submittalLeadLabel(raw, lower, style);
  if (style === "plan") return "Material Plan Lead Time";
  return "Material Lead Time";
}

function offsetLines(
  kind: "submittal" | "material",
  offsets: RawInstance["workflow_instance_offsets"],
  style: "plan" | "plain"
): LeadTimeLine[] {
  return (offsets ?? [])
    .filter((offset) => (offset.duration ?? 0) > 0)
    .map((offset) => ({
      label: leadLabel(kind, offset.name, style),
      days: offset.duration ?? 0
    }));
}

function heading(label: string): LeadTimeLine {
  return { label, days: 0, section: true };
}

function leadHoverStyle(
  governing: GoverningActivity | null
): "plan" | "material" | "submittal" | "none" {
  if (!governing) return "none";
  if (governing.source === "MANUAL") return "plan";
  if (governing.submittal) return "submittal";
  if (governing.material) return "material";
  if (governing.source === "ACTIVITY_LINK") return "plan";
  return "none";
}

/** Breakdown for the same max pair resolveDurations uses as the lead time. */
export function resolveLeadLines(
  pairs: PlanningPair[],
  governing: GoverningActivity | null = null
): LeadTimeLine[] {
  const style = leadHoverStyle(governing);
  if (style === "none") return [];
  let bestTotal = 0;
  let bestIndex = -1;
  pairs.forEach((pair, index) => {
    const sub = sumOffsets(pair.submittal?.workflow_instance_offsets);
    const mat = sumOffsets(pair.material?.workflow_instance_offsets);
    if (sub + mat > bestTotal) {
      bestTotal = sub + mat;
      bestIndex = index;
    }
  });
  const chosen = bestIndex >= 0 ? pairs[bestIndex] : null;
  if (!chosen) return [];
  const labelStyle = style === "plan" ? "plan" : "plain";
  const subLines = offsetLines(
    "submittal",
    chosen.submittal?.workflow_instance_offsets,
    labelStyle
  );
  const leadTime = chosen.material?.feature_workflow_instance?.lead_time;
  const matFromLead =
    style !== "plan" && leadTime != null && Number(leadTime) > 0
      ? [{ label: "Material Lead Time", days: Number(leadTime) }]
      : [];
  const matLines =
    matFromLead.length > 0
      ? matFromLead
      : offsetLines(
          "material",
          chosen.material?.workflow_instance_offsets,
          labelStyle
        );
  if (style === "plan") return [...subLines, ...matLines];
  if (style === "material") {
    return [
      ...(subLines.length
        ? [
            heading(
              "Information of Submittal Plan or Submittal that is used for back computation to the Bid Package"
            ),
            ...subLines
          ]
        : []),
      ...(matLines.length
        ? [
            heading("Information of Material governing Bid Package"),
            ...matLines
          ]
        : [])
    ];
  }
  return [
    ...(chosen.submittal || subLines.length
      ? [
          heading(
            "Information of governing Submittal that is used for back computation to the Bid Package"
          ),
          ...subLines
        ]
      : []),
    ...(matLines.length
      ? [
          heading("Information of Material governing the Submittal"),
          ...matLines
        ]
      : [])
  ];
}

function hasScheduleActivity(inst: RawInstance | null | undefined): boolean {
  return (inst?.feature_activities?.length ?? 0) > 0;
}

const UUID_TITLE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Implicit materials are stored with a UUID title. The planner's log rows
// omit the `implicit` flag, so a UUID title marks one too.
function isImplicit(inst: RawInstance): boolean {
  return inst.implicit === true || UUID_TITLE.test(inst.title?.trim() ?? "");
}

function isRealMaterial(
  inst: RawInstance | null | undefined
): inst is RawInstance & { id: string; title: string } {
  return (
    !!inst?.id &&
    !!inst.title?.trim() &&
    !isImplicit(inst) &&
    inst.feature_type_id === FeatureTypes.MATERIALS
  );
}

// The submittal card names an implicit material after the submittal it was
// created for.
function isImplicitMaterial(
  inst: RawInstance | null | undefined
): inst is RawInstance & { id: string } {
  return (
    !!inst?.id &&
    isImplicit(inst) &&
    inst.feature_type_id === FeatureTypes.MATERIALS
  );
}

function isRealSubmittal(
  inst: RawInstance | null | undefined
): inst is RawInstance & { id: string; title: string } {
  return (
    !!inst?.id &&
    !!inst.title?.trim() &&
    inst.implicit !== true &&
    inst.feature_type_id === FeatureTypes.SUBMITTALS
  );
}

// A schedule activity is any linked target that carries feature_activities.
// Planning-only activities (type 8) are an onsite estimate, not this count.
function isRealActivity(
  inst: RawInstance | null | undefined
): inst is RawInstance & { id: string } {
  if (!inst?.id || !hasScheduleActivity(inst)) return false;
  const planningOnly: number = FeatureTypes.PLANNING_ONLY_ACTIVITY;
  return Number(inst.feature_type_id) !== planningOnly;
}

function linkTarget(link: RawLink): RawInstance | null {
  const feature = link.target_feature_instance ?? null;
  const activities = link.target_activities_instance ?? null;
  if ((feature?.feature_activities?.length ?? 0) > 0) return feature;
  if ((activities?.feature_activities?.length ?? 0) > 0) return activities;
  return feature ?? activities;
}

function activityLinksOf(inst: RawInstance | null | undefined): RawLink[] {
  const named = inst?.linkedActivities ?? [];
  if (named.length > 0) return named;
  return inst?.links_as_source ?? [];
}

function activityTitle(inst: RawInstance): string {
  return inst.title?.trim() || inst.number?.trim() || "";
}

function activityDateOf(link: RawLink): string | null {
  const inst = linkTarget(link);
  if (!inst) return null;
  const finishToStart =
    (link.link_type ?? LINK_TYPE_FINISH_TO_START) === LINK_TYPE_FINISH_TO_START;
  const activity = inst.feature_activities?.[0];
  if (activity) {
    const preferred = finishToStart ? activity.start_date : activity.end_date;
    return preferred ?? activity.start_date ?? activity.end_date ?? null;
  }
  return inst.feature_workflow_instance?.manual_entry_date ?? null;
}

function earliestActivityDate(
  links: RawLink[] | null | undefined
): string | null {
  const dates = (links ?? [])
    .map(activityDateOf)
    .filter((date): date is string => !!date);
  if (dates.length === 0) return null;
  return dates.slice().sort((a, b) => Date.parse(a) - Date.parse(b))[0];
}

function scheduleActivitiesOf(
  links: RawLink[] | null | undefined
): SiteMaterialLink["activities"] {
  return (links ?? [])
    .filter((activityLink) => isRealActivity(linkTarget(activityLink)))
    .map((activityLink) => {
      const inst = linkTarget(activityLink);
      return {
        id: inst?.id ?? "",
        name: activityTitle(inst ?? {}),
        date: activityDateOf(activityLink)
      };
    })
    .filter((activity) => activity.id);
}

function materialCardOf(inst: RawInstance): SiteMaterialLink {
  return {
    id: inst.id ?? "",
    title: inst.title?.trim() || "",
    activities: scheduleActivitiesOf(activityLinksOf(inst))
  };
}

/**
 * Direct bid-package links only: activities (type 9) on the bid package,
 * materials on `linkedMaterials`, submittals on `linkedSubmittals`. Nested
 * materials under a submittal stay on that submittal card, not in the count.
 */
export function resolveSiteLinks(bp: RawInstance | null | undefined): {
  activities: SiteActivityLink[];
  materials: SiteMaterialLink[];
  submittals: SiteSubmittalLink[];
} {
  const activities: SiteActivityLink[] = [];
  const materials: SiteMaterialLink[] = [];
  const submittals: SiteSubmittalLink[] = [];
  const seenActivities = new Set<string>();
  const seenMaterials = new Set<string>();
  const seenSubmittals = new Set<string>();

  const pushRealMaterial = (inst: RawInstance | null | undefined) => {
    if (!isRealMaterial(inst) || seenMaterials.has(inst.id)) return;
    seenMaterials.add(inst.id);
    materials.push(materialCardOf(inst));
  };

  (bp?.linkedSubmittals ?? []).forEach((link) => {
    const inst = link.target_feature_instance;
    if (!inst?.id || seenSubmittals.has(inst.id)) return;
    seenSubmittals.add(inst.id);
    const materialLinks = inst.linkedMaterials ?? [];
    if (!isRealSubmittal(inst)) return;
    submittals.push({
      id: inst.id,
      title: inst.title.trim(),
      materials: materialLinks
        .map((materialLink) => materialLink.target_feature_instance)
        .filter(
          (material): material is RawInstance & { id: string } =>
            isRealMaterial(material) || isImplicitMaterial(material)
        )
        .map((material) => ({
          id: material.id,
          title: isImplicit(material)
            ? getImplicitMaterialDisplayTitle(
                inst.auto_inc_value ?? inst.number
              )
            : (material.title ?? "").trim(),
          date: earliestActivityDate(activityLinksOf(material))
        }))
    });
  });

  (bp?.linkedMaterials ?? []).forEach((link) => {
    pushRealMaterial(link.target_feature_instance);
  });

  // Bid package -> linkedActivities whose target is an Activity (type 9).
  const activityType: number = FeatureTypes.ACTIVITIES;
  activityLinksOf(bp).forEach((activityLink) => {
    const inst = linkTarget(activityLink);
    if (!inst?.id || Number(inst.feature_type_id) !== activityType) return;
    if (seenActivities.has(inst.id)) return;
    seenActivities.add(inst.id);
    activities.push({
      id: inst.id,
      name: activityTitle(inst),
      date: activityDateOf(activityLink),
      materialName: null
    });
  });

  return { activities, materials, submittals };
}

function plannedMilestoneDate(
  inst: RawInstance | null | undefined
): string | null {
  const dated = (inst?.workflow_instance_milestones ?? [])
    .filter((milestone) => !milestone.hidden && milestone.planned_date)
    .slice()
    .sort((a, b) => (a.sequence_no ?? 0) - (b.sequence_no ?? 0));
  return dated.length ? (dated[dated.length - 1].planned_date ?? null) : null;
}

function drivingPlan(pairs: PlanningPair[]): {
  id: string;
  name: string;
  date: string;
  material: { id: string; name: string } | null;
  submittal: { id: string; name: string } | null;
} | null {
  let bestTotal = -1;
  let bestIndex = -1;
  pairs.forEach((pair, index) => {
    const total =
      sumOffsets(pair.submittal?.workflow_instance_offsets) +
      sumOffsets(pair.material?.workflow_instance_offsets);
    if (total > bestTotal) {
      bestTotal = total;
      bestIndex = index;
    }
  });
  const chosen = bestIndex >= 0 ? pairs[bestIndex] : null;
  if (!chosen) return null;
  const materialDate = plannedMilestoneDate(chosen.material);
  if (chosen.material && materialDate) {
    const name = activityTitle(chosen.material);
    return {
      id: chosen.material.id ?? "",
      name,
      date: materialDate,
      material: { id: chosen.material.id ?? "", name },
      submittal: submittalOf(chosen)
    };
  }
  const submittalDate = plannedMilestoneDate(chosen.submittal);
  if (chosen.submittal && submittalDate) {
    const name = activityTitle(chosen.submittal);
    return {
      id: chosen.submittal.id ?? "",
      name,
      date: submittalDate,
      material: null,
      submittal: submittalOf(chosen)
    };
  }
  return null;
}

/**
 * Bid-package activity links (type 9 and the deadline picker type 8) are
 * ignored by getPairs once a submittal or material chain exists. Fold them
 * into governing so a BP-level onsite date still competes.
 */
function pairsForGoverning(
  bp: RawInstance | null | undefined,
  pairs: PlanningPair[]
): PlanningPair[] {
  if (!pairs.some((pair) => pair.submittal || pair.material)) return pairs;
  const direct = activityLinksOf(bp);
  if (direct.length === 0) return pairs;
  return [...pairs, { submittal: null, material: null, activityLinks: direct }];
}

// A linked activity (or a set date) is the direct required-on-site date. When
// that is missing, the driving material or submittal's last planned milestone
// is the on-site date.
export function onSiteGoverning(
  pairs: PlanningPair[]
): GoverningActivity | null {
  const governing = resolveGoverning(pairs);
  const direct =
    governing &&
    (governing.source === "ACTIVITY_LINK" || governing.source === "MANUAL") &&
    governing.startDate;
  if (direct) return governing;
  const plan = drivingPlan(pairs);
  if (!plan) return governing;
  return {
    id: plan.id,
    name: plan.name,
    startDate: plan.date,
    endDate: plan.date,
    isDeleted: false,
    source: "MATERIAL_DEADLINE",
    dateKind: "start",
    material: plan.material,
    submittal: plan.submittal
  };
}

function buildRow(
  link: RawLink,
  dpMilestones: MilestoneNode[],
  span: { start: number; end: number } | null
): BidPackageRow {
  const bp = link.target_feature_instance ?? {};
  // Bid-package bars position by the plan date only.
  const milestones = mapMilestones(bp.workflow_instance_milestones, {
    planOnly: true
  });
  const pairs = getPairs(bp);
  const governing = onSiteGoverning(pairsForGoverning(bp, pairs));
  const { segments: durations, totalDays } = resolveDurations(pairs);
  const siteLinks = resolveSiteLinks(bp);
  const submittalCount = siteLinks.submittals.length;
  const materialCount = siteLinks.materials.length;

  const hasDates = milestones.some((m) => m.effectiveDate);
  const hasPlan = pairs.some((p) => p.submittal || p.material);
  const state: BidPackageRow["state"] =
    hasDates && hasPlan ? "PLANNED" : "NO_PLAN";

  return {
    id: bp.id ?? "",
    number: bp.number ?? "",
    name: bp.title ?? "",
    workflowInstanceId: bp.feature_workflow_instance?.id ?? null,
    milestones,
    linkedDpMilestone: findLinkedMilestone(link, dpMilestones),
    governing,
    durations,
    totalDays,
    state,
    riskLevel: riskLevelOf(bp.feature_workflow_instance?.risk_level),
    milestoneState: milestoneStateOf(
      bp.feature_workflow_instance?.milestone_state
    ),
    effectiveFloat: numberOrNull(bp.feature_workflow_instance?.effective_float),
    workflowStatus: workflowStatusOf(link.workflow_status),
    deadlineDate: bp.feature_workflow_instance?.final_deadline_date ?? null,
    estimatedValue: numberOrNull(
      bp.estimated_value ?? bp.feature_bid_package?.estimated_value
    ),
    submittalCount,
    materialCount,
    activityCount: siteLinks.activities.length,
    leadLines: resolveLeadLines(pairs, governing),
    linkedActivities: siteLinks.activities,
    linkedMaterials: siteLinks.materials,
    linkedSubmittals: siteLinks.submittals,
    outOfScope: outOfScopeOf(milestones, span)
  };
}

function riskLevelOf(raw: string | null | undefined): BidRiskLevel {
  const level = raw?.trim().toUpperCase();
  return level === "HIGH" || level === "MEDIUM" ? level : null;
}

function workflowStatusOf(raw: string | null | undefined): BidWorkflowStatus {
  const status = raw?.trim().toUpperCase();
  if (status === EWorkflowStatusDataBlock.IN_PROGRESS) return "IN_PROGRESS";
  if (status === EWorkflowStatusDataBlock.WF_COMPLETED) return "COMPLETED";
  return "NOT_STARTED";
}

function milestoneStateOf(raw: string | null | undefined): string | null {
  const state = raw?.trim();
  return state || null;
}

function numberOrNull(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function collectAxisDates(
  dpMs: MilestoneNode[],
  rows: BidPackageRow[]
): string[] {
  const dates: string[] = [];
  const push = (d: string | null) => {
    if (d) dates.push(d);
  };
  // Only dates that are positioned on the shared timeline feed the axis. The
  // governing activity is drawn in the fixed right-hand cluster, not on the
  // timeline, so its dates must not stretch the scale.
  dpMs.forEach((m) => push(m.effectiveDate));
  rows.forEach((r) => {
    r.milestones.forEach((m) => push(m.effectiveDate));
  });
  return [...new Set(dates)];
}

export function toDependencyModel(raw: RawDependencyPayload): DependencyModel {
  const dp = raw.feature_instance_by_pk ?? {};
  const dpMilestones = applyWorkflowOffsets(
    mapMilestones(dp.workflow_instance_milestones),
    dp.workflow_instance_offsets
  );
  const dpDays = dpMilestones
    .map((m) => m.effectiveDate)
    .filter((d): d is string => !!d)
    .map(dayNum);
  const designSpan = dpDays.length
    ? { start: Math.min(...dpDays), end: Math.max(...dpDays) }
    : null;
  const rows = (dp.linkedBidPackages ?? []).map((link) =>
    buildRow(link, dpMilestones, designSpan)
  );

  return {
    designPackage: {
      id: dp.id ?? "",
      name: dp.title ?? "",
      workflowInstanceId: dp.feature_workflow_instance?.id ?? null,
      milestones: dpMilestones,
      todayDate: dp.todays_date ?? null,
      currentDelay: numberOrNull(dp.current_delay)
    },
    bidPackages: rows,
    axisDates: collectAxisDates(dpMilestones, rows)
  };
}
